/* eslint-disable import/order */
import { useCallback, useRef, useState } from 'react'
import epsClient from 'enrollment-platform/api/eps.client'
import { message } from 'antd'

type UploadState =
  | 'idle'
  | 'presigning'
  | 'uploading'
  | 'saving'
  | 'success'
  | 'error'

type PresignedUploadUrlDto = {
  presignedUploadUrl: string
  s3Key: string
}

export type EmployeeHandbookLanguage = 'english' | 'spanish'

export type CompleteUploadDto = {
  fileName: string
  s3Key: string
  language: EmployeeHandbookLanguage
  year: number
}

type Options = {
  clientId?: string
  clientPublicKey?: string
  existingEmployeeHandbook?: {
    fileName: string
    s3Key: string
    language: EmployeeHandbookLanguage
    uploadedAt: string
  } | null
  language?: EmployeeHandbookLanguage
  year?: number
  onOk?: (success?: boolean) => void
  onSuccess?: () => void
  onSaved?: (payload: CompleteUploadDto) => void
}

export function useEmployeeHandbookUpload(opts: Options) {
  const clientKey = opts.clientPublicKey ?? opts.clientId ?? ''
  const successCb = opts.onSuccess ?? opts.onOk
  const defaultLanguage: EmployeeHandbookLanguage = opts.language ?? 'english'
  const year = opts.year ?? new Date().getFullYear()

  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [state, setState] = useState<UploadState>('idle')

  const openFilePicker = useCallback(() => {
    if (!clientKey) {
      message.error('Missing customer id.')
      return
    }
    fileInputRef.current?.click()
  }, [clientKey])

  const handleUpload = useCallback(
    async (
      selectedFile: File | null | undefined,
      languageOverride?: EmployeeHandbookLanguage
    ) => {
      if (!selectedFile || !clientKey) {
        message.error('Please select a file and a client before uploading.')
        return
      }

      const languageToUse = languageOverride ?? defaultLanguage
      const existedAtStart = Boolean(opts.existingEmployeeHandbook)

      setIsUploading(true)
      setState('presigning')

      try {
        const { name: fileName, type } = selectedFile

        // 1) PRESIGN – now sending year + language
        const presignRes = await epsClient.post<PresignedUploadUrlDto>(
          `/api/v1/employee-experience/customers/${clientKey}/handbook/presigned-upload-url`,
          {
            fileName,
            language: languageToUse,
            year,
          }
        )

        const { presignedUploadUrl, s3Key } = presignRes.data
        if (!presignedUploadUrl || !s3Key) {
          message.error('An error occurred. Please try again.')
          setState('error')
          return
        }

        // 2) S3 PUT
        setState('uploading')
        const putRes = await fetch(presignedUploadUrl, {
          method: 'PUT',
          body: selectedFile,
          headers: { 'Content-Type': type || 'application/pdf' },
        })
        if (!putRes.ok) {
          const errorText = await putRes.text().catch(() => '')
          console.error('presigned upload failed', putRes.status, errorText)
          message.error('An error occurred. Please try again.')
          setState('error')
          return
        }

        // 3) SAVE (mark upload complete) – send year + language
        setState('saving')
        const payload: CompleteUploadDto = {
          fileName,
          s3Key,
          language: languageToUse,
          year,
        }
        await epsClient.post(
          `/api/v1/employee-experience/customers/${clientKey}/handbook/complete`,
          payload
        )
        opts.onSaved?.(payload)

        // 4) DONE
        setState('success')
        message.success(
          existedAtStart
            ? 'Employee handbook replaced successfully.'
            : 'Employee handbook uploaded and saved.'
        )
        successCb?.(true)
      } catch (error: any) {
        console.error(error)
        message.error(
          `Failed to upload/save. Error: ${error?.message ?? error}`
        )
        setState('error')
      } finally {
        setIsUploading(false)
        if (fileInputRef.current) fileInputRef.current.value = ''
      }
    },
    [
      clientKey,
      defaultLanguage,
      year,
      successCb,
      opts.existingEmployeeHandbook,
      opts.onSaved,
    ]
  )

  const onFileChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    async (e) => {
      const file = e.target.files?.[0] ?? null
      await handleUpload(file)
    },
    [handleUpload]
  )

  const exists = Boolean(opts.existingEmployeeHandbook)

  const buttonLabel =
    state === 'presigning'
      ? 'Getting URL…'
      : state === 'uploading'
        ? 'Uploading…'
        : state === 'saving'
          ? 'Saving…'
          : exists
            ? 'Replace Employee Handbook'
            : 'Upload Employee Handbook'

  return {
    fileInputRef,
    openFilePicker,
    onFileChange,
    handleUpload,
    isUploading,
    state,
    buttonLabel,
  }
}
